import { ClientType, IClientFactory } from "@multiweb/clients/index";
import { DIContainer } from "Global/Base/DIContainer";
import { CancellationToken } from "Global/Base/Promises/CancellationTokenSource";
import { RequestContext } from "OW.Remoting/Remoting/RequestContext";
import { convertContextFromFrameToRequest } from "UI/UI/Utilities/SharedUIMethods";
import { UIUtils } from "../../../../Global/Dom/UIUtils";
import { OWManager } from "../../../../OW.Remoting/Remoting/OWManager";
import { ButtonColumn } from "../../../UI.Controls/DataGridSubcontrols/Columns/ButtonColumn";
import { Column } from "../../../UI.Controls/DataGridSubcontrols/Columns/Column";
import { DateTimeColumn } from "../../../UI.Controls/DataGridSubcontrols/Columns/DateTimeColumn";
import { IListDataRowElement } from "../../../UI.Controls/DataGridSubcontrols/Components/IDataRow";
import { DocumentVersionsDataSource } from "../../../UI.Controls/DataSource/DocumentVersionsDataSource";
import { ListDataGrid } from "../../../UI.Controls/ListDataGrid";
import { createElement } from "../../../UI.ElementarControls/Utils/CustomElementUtils";
import { IFRMParams } from "../Controls/FormParameters";
import { DownloadManager } from "../DownloadManager";
import { FormManager } from "../FormManager/IFormManager";
import { FrameContext, IWindowable } from "../UIManagerInterfaces";
import { IShowArchiveVersionsFormBuilder } from "./IFormBuilder";
import { ProgrammableForm } from "./ProgrammableForm";

export class ShowArchiveVersionsFormBuilder implements IShowArchiveVersionsFormBuilder {
    constructor() {
        // Prázdný konstruktor
    }

    private _frameContext: FrameContext;

    /**
     * Sestaví formulář pro zobrazení verzí dokumentu
     * @param className Třída objektu
     * @param id ID objektu
     * @param instanceID Instance objektu
     * @param attrName Atribut objektu
     * @param parametricData Volitelné parametry formuláře
     */
    public async build(className: string, id: string, instanceID: string, attrName: string, parametricData: IFRMParams, frameContext: FrameContext): Promise<IWindowable> {
        const formCode = "dynamicFrm.documentversions";
        const formInstanceId = FormManager.instance.createFormInstanceId(formCode, parametricData);
        const form = new ProgrammableForm(formCode, formInstanceId, parametricData);
        this._frameContext = frameContext;
        form.isModal = true;
        form.titleLocKey = "GUI-678599"; //Verze dokumentu

        await FormManager.instance.openCustomModalForm(form);
        const dataGridWrapper = await this._createDataGrid(className, id, instanceID, attrName);
        await form.init(form.formUI);
        form.addControl(dataGridWrapper.element);

        return form;
    }

    protected _getRequestContext(cancellationToken: CancellationToken = CancellationToken.none): RequestContext {
        return convertContextFromFrameToRequest(this._frameContext, cancellationToken);
    }

    private async _createDataGrid(className: string, id: string, instanceID: string, attrName: string): Promise<SimpleViewDataGrid<IListDataRowElement>> {
        const dgWrapper = new SimpleViewDataGrid(new ListDataGrid<IListDataRowElement>());
        dgWrapper.dataGrid.dataSource = this._createDataSource(instanceID, attrName);
        dgWrapper.dataGrid.headerRow.disableSort = true;

        //Kopírovat odkaz
        let btnCol = this._createColumn(ButtonColumn, "CopyUrl", "", 150);
        btnCol.buttonLocKey = "GUI-678741"; //Kopirovat odkaz
        btnCol.action = async (version: string) => UIUtils.pasteToClipboard(OWManager.getPublicDocumentDownloadUrl(className, id, attrName, version));
        dgWrapper.columns.push(btnCol);

        //Číslo verze
        dgWrapper.columns.push(this._createColumn(Column, "Number", "GUI-362729", 150));

        //Název souboru
        btnCol = this._createColumn(ButtonColumn, "Name", "GUI-597167", 300);
        btnCol.action = async (version: string) => await DownloadManager.downloadFile(instanceID, attrName, version, () => { /* Prázdná arrow funkce */ });
        dgWrapper.columns.push(btnCol);

        //Autor
        dgWrapper.columns.push(this._createColumn(Column, "Author", "GUI-362735", 150));

        //Datum a čas vytvoření
        dgWrapper.columns.push(this._createColumn(DateTimeColumn, "CreateTime", "GUI-678618", 150));

        await dgWrapper.initialize();
        return dgWrapper;
    }

    private _createColumn<T extends Column>(type: { new (): T }, attrName: string, locKey: string, width?: number, locParams?: string[]): T {
        const col = new type();
        col.attrName = attrName;
        col.locKey = locKey;
        col.locParams = locParams;
        col.width = width;
        col.hideColumnMenu = true;
        return col;
    }

    private _createDataSource(instanceID: string, attrName: string): DocumentVersionsDataSource {
        const ds = new DocumentVersionsDataSource(instanceID, attrName, DIContainer.instance.create(IClientFactory).get(ClientType.document), this._frameContext);
        ds.setAttrs(["ID", "ArchiveVersionDocID", "ArchiveVersionName", "Name"]);
        return ds;
    }
}

/**
 * Wrapper pro jednoduché zobrazení ListDataGridu
 */
class SimpleViewDataGrid<R extends IListDataRowElement> {
    private _dataGrid: ListDataGrid<R>;
    private _columns: Column[];

    private _element: HTMLElement;

    constructor(datagrid: ListDataGrid<R>) {
        this._dataGrid = datagrid;
        this._columns = [];
        this._element = createElement({ classes: "dataGrid simple-view" });
        this._element.appendChild(datagrid);
    }

    public async initialize(): Promise<void> {
        this.dataGrid.columns = this._columns;
        await this.dataGrid.initialize();
        this.dataGrid.fetchCount = undefined;
        await this.dataGrid.dataSource.loadNewData();
    }
    public get columns(): Column[] {
        return this._columns;
    }
    public get dataGrid(): ListDataGrid<R> {
        return this._dataGrid;
    }

    public get element(): HTMLElement {
        return this._element;
    }
}
